import { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import { Smile, Paperclip, Hash, X, Image as ImageIcon, FileText, Type, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Tooltip } from '@/components/ui/tooltip';
import { useMessageStore } from '@/stores/message-store';
import { useUIStore } from '@/stores/ui-store';
import { useTextSelectionToolbar } from '@/hooks/use-text-selection-toolbar';
import { TemplatePicker } from './template-picker';
import { TextFormattingToolbar } from '@/components/common/text-formatting-toolbar';

// Simple emoji categories with commonly used emojis
const EMOJI_CATEGORIES = [
  {
    label: 'Smileys',
    emojis: ['😊', '😂', '🥰', '😍', '🤩', '😎', '🥳', '😄', '😅', '🤗', '😘', '😜', '🤔', '😴', '😭', '😱', '🙏', '❤️', '👋', '👍'],
  },
  {
    label: 'Business',
    emojis: ['📞', '📱', '💼', '📧', '📋', '✅', '❌', '⏰', '📅', '🔔', '💡', '🎯', '📊', '💬', '🔗', '📌', '⭐', '🏷️', '💰', '🎉'],
  },
];

/**
 * Build highlighted HTML from body text.
 * Renders the EXACT same characters but wraps {{placeholder}} in a highlight span
 * and applies visual formatting for WhatsApp markers (*bold*, _italic_, ~strike~).
 * Since characters are identical to the textarea, positions match perfectly.
 */
function buildHighlightMarkup(body: string): string {
  // Escape HTML entities, then wrap {{placeholders}} in styled marks
  const escaped = body
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  let highlighted = escaped;

  // Apply WhatsApp-style formatting — markers stay visible but styled
  // Bold: *text* → marker dims, text bold
  highlighted = highlighted.replace(
    /\*([^*\n]+)\*/g,
    '<span class="opacity-40">*</span><span class="font-bold">$1</span><span class="opacity-40">*</span>'
  );
  // Italic: _text_ → marker dims, text italic
  highlighted = highlighted.replace(
    /(?<![a-zA-Z0-9])_([^_\n]+)_(?![a-zA-Z0-9])/g,
    '<span class="opacity-40">_</span><span class="italic">$1</span><span class="opacity-40">_</span>'
  );
  // Strikethrough: ~text~ → marker dims, text line-through
  highlighted = highlighted.replace(
    /~([^~\n]+)~/g,
    '<span class="opacity-40">~</span><span class="line-through">$1</span><span class="opacity-40">~</span>'
  );

  // Placeholders: {{name}}
  highlighted = highlighted.replace(
    /\{\{([\w-]+)\}\}/g,
    '<mark class="bg-primary/20 text-primary rounded px-0 py-0 font-medium">{{$1}}</mark>'
  );

  // Workflow module fields: ${MODULE.FIELD} or ${MODULE.PREFIX_FIELD}
  highlighted = highlighted.replace(
    /\$\{([A-Z0-9_]+\.[A-Za-z0-9_]+)\}/g,
    '<mark class="bg-primary/20 text-primary rounded px-0 py-0 font-medium">${$1}</mark>'
  );

  // Add trailing newline so the highlight div height matches textarea when ending with \n
  return highlighted + '\n';
}

/** Check if body contains any WhatsApp formatting markers */
function hasFormattingMarkers(body: string): boolean {
  return /\*[^*\n]+\*|(?<![a-zA-Z0-9])_[^_\n]+_(?![a-zA-Z0-9])|~[^~\n]+~/.test(body);
}

/** Extract unique placeholder names from body */
function extractPlaceholderNames(body: string): string[] {
  const names = new Set<string>();
  const pattern = /\{\{([\w-]+)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(body)) !== null) {
    names.add(match[1]);
  }
  return Array.from(names);
}

/**
 * Find all placeholder ranges in the body text.
 * Returns array of { start, end, name } for each {{placeholder}}.
 */
function findAllPlaceholders(body: string): Array<{ start: number; end: number; name: string }> {
  const results: Array<{ start: number; end: number; name: string }> = [];
  const pattern = /\{\{([\w-]+)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(body)) !== null) {
    results.push({ start: match.index, end: match.index + match[0].length, name: match[1] });
  }
  return results;
}

/**
 * Check if a cursor position falls inside any placeholder.
 * Returns the placeholder range if cursor is strictly inside (between start and end exclusive),
 * or null if cursor is outside all placeholders.
 */
function findPlaceholderAt(body: string, cursorPos: number): { start: number; end: number; name: string } | null {
  const placeholders = findAllPlaceholders(body);
  for (const p of placeholders) {
    // Cursor is inside if it's after the start and before the end (exclusive boundaries)
    if (cursorPos > p.start && cursorPos < p.end) {
      return p;
    }
  }
  return null;
}

/**
 * Check if a cursor position is right at the end of a placeholder (for Backspace).
 * Returns the placeholder if cursor is exactly at p.end.
 */
function findPlaceholderEndingAt(body: string, cursorPos: number): { start: number; end: number; name: string } | null {
  const placeholders = findAllPlaceholders(body);
  for (const p of placeholders) {
    if (cursorPos === p.end) return p;
  }
  return null;
}

/**
 * Check if a cursor position is right at the start of a placeholder (for Delete key).
 * Returns the placeholder if cursor is exactly at p.start.
 */
function findPlaceholderStartingAt(body: string, cursorPos: number): { start: number; end: number; name: string } | null {
  const placeholders = findAllPlaceholders(body);
  for (const p of placeholders) {
    if (cursorPos === p.start) return p;
  }
  return null;
}

interface MessageInputProps {
  className?: string;
  /**
   * When provided, overrides the recipient-based `#` autocomplete with these
   * module field options. Used in workflow mode for CRM field insertion.
   */
  workflowFields?: Array<{ value: string; label: string }>;
  /**
   * When true, the selected template cannot be changed/cleared — only
   * placeholder variables can be edited. Used when editing an existing
   * webhook template (the template itself is locked).
   */
  lockTemplate?: boolean;
}

export function MessageInput({ className, workflowFields, lockTemplate }: MessageInputProps) {
  const body = useMessageStore((s) => s.body);
  const setBody = useMessageStore((s) => s.setBody);
  const selectedTemplate = useMessageStore((s) => s.selectedTemplate);
  const templateVariables = useMessageStore((s) => s.templateVariables);
  const attachedFile = useMessageStore((s) => s.attachedFile);
  const setAttachedFile = useMessageStore((s) => s.setAttachedFile);
  const recipients = useMessageStore((s) => s.recipients);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);

  // Single CRM-record context: one unique entityId among CRM recipients
  // (a single record can produce multiple recipients — one per phone
  // field — so checking recipients.length === 1 misses the common case
  // of a record with both Phone and Mobile). In this context the #
  // picker should insert the resolved value directly (e.g. "John
  // Smith") instead of a `{{First_Name}}` placeholder — there's nothing
  // to substitute per-recipient downstream, so the placeholder would
  // leak through the send unchanged. Matches the behavior already in
  // chat-input.tsx.
  const crmRecipients = useMemo(
    () => recipients.filter((r) => r.source === 'crm'),
    [recipients],
  );
  const uniqueCrmEntityIds = useMemo(
    () => new Set(crmRecipients.map((r) => r.entityId).filter(Boolean)),
    [crmRecipients],
  );
  const isSingleRecordContext = crmRecordContext && uniqueCrmEntityIds.size === 1 && !!crmRecipients[0];
  const propertyValues = useMemo(() => {
    if (!isSingleRecordContext) return {} as Record<string, string>;
    const r = crmRecipients[0];
    const map: Record<string, string> = { ...(r.properties || {}) };
    if (r.name) map['name'] = r.name;
    if (r.phone) map['phone'] = r.phone;
    if (r.email) map['email'] = r.email;
    return map;
  }, [isSingleRecordContext, crmRecipients]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const placeholderDropdownRef = useRef<HTMLDivElement>(null);
  const [showEmojis, setShowEmojis] = useState(false);
  const [charWarning, setCharWarning] = useState(false);

  // # placeholder autocomplete state
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [placeholderFilter, setPlaceholderFilter] = useState('');
  const [triggerIndex, setTriggerIndex] = useState(-1);
  const [selectedPropIndex, setSelectedPropIndex] = useState(0);

  // Compute available properties from recipients (union of all property keys)
  // Only show when at least one CRM or CSV recipient exists (manual-only has no useful properties)
  const availableProperties = useMemo(() => {
    // Workflow mode: use module field values directly
    if (workflowFields && workflowFields.length > 0) {
      return workflowFields.map((f) => f.value).sort();
    }

    const hasCrmOrCsv = recipients.some((r) => r.source === 'crm' || r.source === 'csv');
    if (!hasCrmOrCsv) return [];

    const keySet = new Set<string>();
    // Track whether any properties have sheet prefixes
    let hasSheetPrefixed = false;
    for (const r of recipients) {
      if (r.properties) {
        for (const k of Object.keys(r.properties)) {
          keySet.add(k);
          if (/^sheet-\d+-/.test(k)) hasSheetPrefixed = true;
        }
      }
      // Add standard fields only for CRM recipients (not CSV with sheet prefixes)
      if (r.source === 'crm') {
        keySet.add('name');
        keySet.add('phone');
        if (r.email) keySet.add('email');
      }
    }
    // If no sheet-prefixed properties exist, add standard fields for all recipients
    if (!hasSheetPrefixed) {
      for (const r of recipients) {
        keySet.add('name');
        keySet.add('phone');
        if (r.email) keySet.add('email');
      }
    }
    return Array.from(keySet).sort();
  }, [recipients, workflowFields]);

  // Build lookup map for workflow field labels
  const workflowFieldLabelMap = useMemo(() => {
    if (!workflowFields) return null;
    const map = new Map<string, string>();
    workflowFields.forEach((f) => map.set(f.value, f.label));
    return map;
  }, [workflowFields]);

  // Group properties by sheet tag (sheet-1-*, sheet-2-*, ...) for per-sheet # buttons
  const sheetPropertyGroups = useMemo(() => {
    const groups: Array<{ tag: string; label: string; properties: string[] }> = [];
    const sheetMap = new Map<string, Set<string>>();
    const nonSheetKeys = new Set<string>();

    for (const key of availableProperties) {
      const match = key.match(/^(sheet-\d+)-(.+)$/);
      if (match) {
        const tag = match[1];
        if (!sheetMap.has(tag)) sheetMap.set(tag, new Set());
        sheetMap.get(tag)!.add(key);
      } else {
        nonSheetKeys.add(key);
      }
    }

    // Non-sheet properties (CRM, manual) as default group
    if (nonSheetKeys.size > 0) {
      groups.push({ tag: '', label: '#', properties: Array.from(nonSheetKeys).sort() });
    }
    // Sheet groups sorted by tag
    for (const [tag, keys] of Array.from(sheetMap.entries()).sort()) {
      const num = tag.replace('sheet-', '');
      groups.push({ tag, label: `#${num}`, properties: Array.from(keys).sort() });
    }
    return groups;
  }, [availableProperties]);

  /** When a per-sheet # button is clicked, filter dropdown to that sheet's properties */
  const [activeSheetFilter, setActiveSheetFilter] = useState<string | null>(null);

  const filteredProperties = useMemo(() => {
    let pool = availableProperties;
    // When a sheet button was clicked, only show that sheet's properties
    if (activeSheetFilter !== null) {
      if (activeSheetFilter === '') {
        // Non-sheet group: exclude sheet-* properties
        pool = pool.filter((p) => !/^sheet-\d+-/.test(p));
      } else {
        pool = pool.filter((p) => p.startsWith(`${activeSheetFilter}-`));
      }
    }
    if (!placeholderFilter) return pool;
    const q = placeholderFilter.toLowerCase();
    return pool.filter((p) => p.toLowerCase().includes(q));
  }, [availableProperties, placeholderFilter, activeSheetFilter]);

  const hasPlaceholdersInBody = /\{\{[\w-]+\}\}/.test(body);
  const hasFormatting = hasFormattingMarkers(body);
  const needsHighlightBackdrop = hasPlaceholdersInBody || hasFormatting;

  // / at start of message triggers template picker dialog
  const [slashTriggeredOpen, setSlashTriggeredOpen] = useState(false);

  // Text formatting toolbar hook
  const formattingToolbar = useTextSelectionToolbar({
    textareaRef,
    text: body,
    setText: setBody,
  });

  // Clear sheet filter when dropdown closes
  useEffect(() => {
    if (!showPlaceholders) setActiveSheetFilter(null);
  }, [showPlaceholders]);

  // Close placeholder dropdown on outside click
  useEffect(() => {
    if (!showPlaceholders) return;
    const handler = (e: MouseEvent) => {
      if (
        placeholderDropdownRef.current &&
        !placeholderDropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowPlaceholders(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPlaceholders]);

  // Sync highlight div scroll with textarea scroll
  const syncScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : '';
    setAttachedFile({ file, previewUrl });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [setAttachedFile]);

  const SMS_LIMIT = 160;
  const SMS_MULTIPART_LIMIT = 1600;

  const insertAtCursor = useCallback(
    (text: string) => {
      const el = textareaRef.current;
      if (!el) {
        setBody(body + text);
        return;
      }
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const newValue = body.substring(0, start) + text + body.substring(end);
      setBody(newValue);
      // Restore cursor position after insert
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + text.length;
        el.focus();
      });
    },
    [body, setBody]
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const el = e.target;

    // Guard: Detect if any existing placeholder was partially broken by this edit
    // Compare the set of valid placeholders before and after
    const oldPlaceholders = findAllPlaceholders(body);
    const newPlaceholders = findAllPlaceholders(val);

    if (oldPlaceholders.length > 0) {
      // Check for broken placeholder patterns — partial {{ or }} sequences that were intact before
      const brokenPattern = /\{\{(?![\w-]+\}\})|(?<!\{\{[\w-]*)\}\}/;
      if (brokenPattern.test(val) && !brokenPattern.test(body)) {
        // A placeholder was partially edited — find which one was broken and remove it entirely
        // Determine what was removed/changed by comparing old body to new value
        const newNames = new Set(newPlaceholders.map((p) => p.name));

        // Find placeholder names that existed before but are now broken
        for (const p of oldPlaceholders) {
          if (!newNames.has(p.name) ||
              oldPlaceholders.filter(op => op.name === p.name).length !==
              newPlaceholders.filter(np => np.name === p.name).length) {
            // This placeholder was broken — remove the partial remnants
            // Clean up any broken {{ or }} from the value
            let cleanedVal = val
              .replace(/\{\{(?![\w-]+\}\})/g, '')  // Remove orphan {{
              .replace(/(?<!\{\{[\w-]*)\}\}/g, ''); // Remove orphan }}

            setBody(cleanedVal);
            setCharWarning(cleanedVal.length > SMS_MULTIPART_LIMIT);

            requestAnimationFrame(() => {
              // Place cursor at a reasonable position
              const pos = Math.min(el.selectionStart, cleanedVal.length);
              el.selectionStart = el.selectionEnd = pos;
            });

            setShowPlaceholders(false);
            return;
          }
        }
      }
    }

    setBody(val);
    setCharWarning(val.length > SMS_MULTIPART_LIMIT);

    // Detect / at start of message → open template picker dialog
    // (disabled when template is locked, e.g. webhook edit mode)
    if (val === '/' && !lockTemplate) {
      setBody('');
      setSlashTriggeredOpen(true);
      return;
    }

    // Detect # trigger for placeholder autocomplete
    if (availableProperties.length > 0) {
      const cursorPos = el.selectionStart;
      const textBeforeCursor = val.substring(0, cursorPos);
      const hashIndex = textBeforeCursor.lastIndexOf('#');

      if (hashIndex >= 0) {
        const textAfterHash = textBeforeCursor.substring(hashIndex + 1);
        // Only show dropdown if no spaces/newlines in the filter text
        if (!textAfterHash.includes(' ') && !textAfterHash.includes('\n')) {
          setPlaceholderFilter(textAfterHash);
          setTriggerIndex(hashIndex);
          setActiveSheetFilter(null); // keyboard # shows ALL properties
          setShowPlaceholders(true);
          setSelectedPropIndex(0);
          return;
        }
      }
    }
    setShowPlaceholders(false);
  };

  // Insert a selected property.
  // Workflow mode: `${MODULE.FIELD}` placeholder for the CF runtime to
  // resolve. Single CRM-record context: insert the actual resolved
  // value directly (no per-recipient substitution to do — leaving a
  // `{{prop}}` placeholder would leak through to the wire). Multi-
  // record / CSV / manual: `{{property}}` placeholder for per-recipient
  // substitution at send time.
  const insertProperty = useCallback(
    (property: string) => {
      const el = textareaRef.current;
      if (!el) return;
      const before = body.substring(0, triggerIndex);
      const after = body.substring(el.selectionStart);
      let insertText: string;
      if (workflowFields && workflowFields.length > 0) {
        insertText = `\${${property}}`;
      } else if (isSingleRecordContext && propertyValues[property]) {
        insertText = propertyValues[property];
      } else {
        insertText = `{{${property}}}`;
      }
      const newBody = before + insertText + after;
      setBody(newBody);
      setCharWarning(newBody.length > SMS_MULTIPART_LIMIT);

      const newPos = triggerIndex + insertText.length;
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = newPos;
        el.focus();
      });
      setShowPlaceholders(false);
    },
    [body, setBody, triggerIndex, workflowFields, isSingleRecordContext, propertyValues]
  );

  // Remove a specific placeholder from the body
  const removePlaceholder = useCallback(
    (placeholderName: string) => {
      // Remove the first occurrence of {{placeholderName}}
      const pattern = `{{${placeholderName}}}`;
      const idx = body.indexOf(pattern);
      if (idx >= 0) {
        const newBody = body.substring(0, idx) + body.substring(idx + pattern.length);
        setBody(newBody);
        requestAnimationFrame(() => {
          textareaRef.current?.focus();
        });
      }
    },
    [body, setBody]
  );

  // Keyboard navigation for dropdowns + atomic placeholder protection
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 1. Handle placeholder dropdown navigation first
    if (showPlaceholders && filteredProperties.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedPropIndex((i) => Math.min(i + 1, filteredProperties.length - 1));
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedPropIndex((i) => Math.max(i - 1, 0));
        return;
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertProperty(filteredProperties[selectedPropIndex]);
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowPlaceholders(false);
        return;
      }
    }

    // 2. Atomic placeholder protection — prevent partial editing of {{placeholders}}
    const el = textareaRef.current;
    if (!el || !hasPlaceholdersInBody) return;

    const cursorPos = el.selectionStart;
    const selEnd = el.selectionEnd;
    const hasSelection = cursorPos !== selEnd;

    if (e.key === 'Backspace' && !hasSelection) {
      // If cursor is right after a placeholder end, delete the whole placeholder
      const placeholderAtEnd = findPlaceholderEndingAt(body, cursorPos);
      if (placeholderAtEnd) {
        e.preventDefault();
        const newBody = body.substring(0, placeholderAtEnd.start) + body.substring(placeholderAtEnd.end);
        setBody(newBody);
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = placeholderAtEnd.start;
          el.focus();
        });
        return;
      }
      // If cursor is inside a placeholder, delete the whole placeholder
      const insidePlaceholder = findPlaceholderAt(body, cursorPos);
      if (insidePlaceholder) {
        e.preventDefault();
        const newBody = body.substring(0, insidePlaceholder.start) + body.substring(insidePlaceholder.end);
        setBody(newBody);
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = insidePlaceholder.start;
          el.focus();
        });
        return;
      }
    }

    if (e.key === 'Delete' && !hasSelection) {
      // If cursor is right before a placeholder start, delete the whole placeholder
      const placeholderAtStart = findPlaceholderStartingAt(body, cursorPos);
      if (placeholderAtStart) {
        e.preventDefault();
        const newBody = body.substring(0, placeholderAtStart.start) + body.substring(placeholderAtStart.end);
        setBody(newBody);
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = placeholderAtStart.start;
          el.focus();
        });
        return;
      }
      // If cursor is inside a placeholder, delete the whole placeholder
      const insidePlaceholder = findPlaceholderAt(body, cursorPos);
      if (insidePlaceholder) {
        e.preventDefault();
        const newBody = body.substring(0, insidePlaceholder.start) + body.substring(insidePlaceholder.end);
        setBody(newBody);
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = insidePlaceholder.start;
          el.focus();
        });
        return;
      }
    }

    if (e.key === 'ArrowLeft' && !hasSelection) {
      // If cursor is inside a placeholder, jump to the start of it
      const insidePlaceholder = findPlaceholderAt(body, cursorPos);
      if (insidePlaceholder) {
        e.preventDefault();
        el.selectionStart = el.selectionEnd = insidePlaceholder.start;
        return;
      }
      // If cursor is right at the end of a placeholder, jump to its start
      const placeholderAtEnd = findPlaceholderEndingAt(body, cursorPos);
      if (placeholderAtEnd) {
        e.preventDefault();
        el.selectionStart = el.selectionEnd = placeholderAtEnd.start;
        return;
      }
    }

    if (e.key === 'ArrowRight' && !hasSelection) {
      // If cursor is inside a placeholder, jump to the end of it
      const insidePlaceholder = findPlaceholderAt(body, cursorPos);
      if (insidePlaceholder) {
        e.preventDefault();
        el.selectionStart = el.selectionEnd = insidePlaceholder.end;
        return;
      }
      // If cursor is right at the start of a placeholder, jump to its end
      const placeholderAtStart = findPlaceholderStartingAt(body, cursorPos);
      if (placeholderAtStart) {
        e.preventDefault();
        el.selectionStart = el.selectionEnd = placeholderAtStart.end;
        return;
      }
    }

    // If cursor is inside a placeholder and user types a regular character, snap cursor out first
    if (!hasSelection && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const insidePlaceholder = findPlaceholderAt(body, cursorPos);
      if (insidePlaceholder) {
        // Move cursor to end of placeholder, then let the character be typed there
        el.selectionStart = el.selectionEnd = insidePlaceholder.end;
      }
    }
  };

  // Auto-resize textarea
  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 300) + 'px';
    }
  };

  // When user clicks inside a placeholder, snap cursor to nearest boundary
  const handleMouseUp = useCallback(() => {
    const el = textareaRef.current;
    if (!el || !hasPlaceholdersInBody) return;

    const cursorPos = el.selectionStart;
    const selEnd = el.selectionEnd;

    // If there's a selection that spans across a placeholder, adjust it
    if (cursorPos !== selEnd) {
      // Don't interfere with selections — users may want to select text including placeholders
      return;
    }

    const insidePlaceholder = findPlaceholderAt(body, cursorPos);
    if (insidePlaceholder) {
      // Snap to the nearer boundary
      const distToStart = cursorPos - insidePlaceholder.start;
      const distToEnd = insidePlaceholder.end - cursorPos;
      const snapPos = distToStart <= distToEnd ? insidePlaceholder.start : insidePlaceholder.end;
      el.selectionStart = el.selectionEnd = snapPos;
    }
  }, [body, hasPlaceholdersInBody]);

  const smsSegments = Math.ceil(body.length / SMS_LIMIT) || 0;
  const hasTemplate = !!selectedTemplate;
  const highlightMarkup = useMemo(() => buildHighlightMarkup(body), [body]);
  const placeholderNames = useMemo(() => extractPlaceholderNames(body), [body]);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label + Template picker */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-primary">Message</Label>
        <TemplatePicker
          externalOpen={slashTriggeredOpen}
          onExternalOpenChange={setSlashTriggeredOpen}
          lockTemplate={lockTemplate}
          workflowFields={workflowFields}
        />
      </div>

      {hasTemplate ? (
        <TemplateReadOnly body={body} templateVariables={templateVariables} />
      ) : (
        <div className="relative group">
          {/*
            Highlight-behind technique:
            1. A div behind the textarea renders the same text with {{placeholders}} highlighted
            2. The textarea sits on top with transparent text (but visible caret)
            3. Since both render identical characters, positions match perfectly
          */}

          {/* Highlight backdrop — renders behind the textarea */}
          {needsHighlightBackdrop && (
            <div
              ref={highlightRef}
              className="absolute inset-0 px-3.5 py-3 pr-14 text-sm leading-relaxed whitespace-pre-wrap break-words overflow-hidden pointer-events-none rounded-lg"
              style={{ wordBreak: 'break-word' }}
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: highlightMarkup }}
            />
          )}

          {/* Actual textarea — transparent text when highlights are active */}
          <textarea
            data-testid="message-input"
            ref={textareaRef}
            value={body}
            onChange={handleChange}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onMouseUp={handleMouseUp}
            onScroll={syncScroll}
            placeholder={availableProperties.length > 0
              ? 'Type your message... (/ templates, # placeholders)'
              : 'Type your message... (/ for templates)'}
            rows={5}
            className={cn(
              'w-full rounded-lg border border-input px-3.5 py-3 pr-14 text-sm leading-relaxed resize-none transition-all',
              'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
              'placeholder:text-muted-foreground/60',
              charWarning && 'border-warning ring-warning/20',
              needsHighlightBackdrop
                ? 'text-transparent caret-foreground bg-transparent'
                : 'bg-background'
            )}
          />

          {/* Text formatting toolbar */}
          <TextFormattingToolbar
            visible={formattingToolbar.visible}
            position={formattingToolbar.position}
            onFormat={formattingToolbar.applyFormat}
          />

          {/* # Placeholder autocomplete dropdown */}
          {showPlaceholders && filteredProperties.length > 0 && (
            <div
              ref={placeholderDropdownRef}
              className="absolute bottom-12 left-0 w-96 max-h-48 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl z-50 backdrop-blur-sm"
            >
              <div className="px-3.5 py-2 border-b border-border bg-muted/30">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {workflowFields && workflowFields.length > 0 ? 'Module Fields' : 'Recipient Fields'}
                </p>
              </div>
              {filteredProperties.map((prop, idx) => {
                const isWorkflow = workflowFields && workflowFields.length > 0;
                const displayLabel = isWorkflow
                  ? workflowFieldLabelMap?.get(prop) || prop
                  : prop;
                const placeholderPreview = isWorkflow ? `\${${prop}}` : `{{${prop}}}`;
                return (
                  <button
                    key={prop}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent textarea blur
                      insertProperty(prop);
                    }}
                    className={cn(
                      'w-full text-left px-3.5 py-2.5 text-sm transition-colors flex items-center gap-2.5',
                      idx === selectedPropIndex
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted text-foreground'
                    )}
                  >
                    <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1 font-medium">{displayLabel}</span>
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0 bg-muted px-1.5 py-0.5 rounded">
                      {placeholderPreview}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Toolbar — floating bottom right inside textarea */}
          <div className="absolute bottom-2.5 right-2.5 flex items-center gap-0.5 rounded-lg bg-background/80 backdrop-blur-sm border border-border/50 p-0.5 shadow-sm z-10">
            {/* Per-sheet placeholder buttons (or single # when no sheets) */}
            {sheetPropertyGroups.map((group) => (
              <Tooltip key={group.tag} content={group.tag ? `Insert ${group.tag} placeholder` : 'Insert placeholder (#)'}>
                <button
                  type="button"
                  onClick={() => {
                    const el = textareaRef.current;
                    if (!el) return;
                    const start = el.selectionStart;
                    const end = el.selectionEnd;
                    const newValue = body.substring(0, start) + '#' + body.substring(end);
                    setBody(newValue);
                    const newPos = start + 1;
                    // Open the autocomplete dropdown filtered to this sheet's properties
                    setActiveSheetFilter(group.tag);
                    setTriggerIndex(start);
                    setPlaceholderFilter('');
                    setShowPlaceholders(true);
                    setSelectedPropIndex(0);
                    requestAnimationFrame(() => {
                      el.selectionStart = el.selectionEnd = newPos;
                      el.focus();
                    });
                  }}
                  className={cn(
                    'rounded-md p-1.5 transition-all flex items-center gap-0.5',
                    showPlaceholders && activeSheetFilter === group.tag
                      ? 'bg-primary/15 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Hash className="h-4 w-4" />
                  {group.tag && <span className="text-[9px] font-bold leading-none">{group.tag.replace('sheet-', '')}</span>}
                </button>
              </Tooltip>
            ))}

            <Tooltip content="Insert emoji">
              <button
                type="button"
                data-testid="compose-emoji-btn"
                onClick={() => setShowEmojis(!showEmojis)}
                className={cn(
                  'rounded-md p-1.5 transition-all',
                  showEmojis
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <Smile className="h-4 w-4" />
              </button>
            </Tooltip>

            <Tooltip content="Attach file">
              <button
                type="button"
                data-testid="compose-attachment-btn"
                onClick={handleFileClick}
                className={cn(
                  'rounded-md p-1.5 transition-all',
                  attachedFile
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <Paperclip className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            className="hidden"
            onChange={handleFileSelected}
          />
        </div>
      )}

      {/* Attached file preview */}
      {attachedFile && !hasTemplate && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-4 py-3 transition-all">
          {(attachedFile.previewUrl || attachedFile.remoteUrl) ? (
            <img
              src={attachedFile.previewUrl || attachedFile.remoteUrl}
              alt="Preview"
              className="h-12 w-12 rounded-lg object-cover ring-1 ring-border"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted ring-1 ring-border">
              <ImageIcon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {attachedFile.file?.name || attachedFile.name || 'Saved media'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {attachedFile.file
                ? `${(attachedFile.file.size / 1024).toFixed(1)} KB`
                : attachedFile.remoteUrl
                  ? 'Remote file'
                  : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAttachedFile(null)}
            className="shrink-0 rounded-lg p-1.5 hover:bg-muted text-muted-foreground hover:text-destructive transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Emoji picker */}
      {showEmojis && !hasTemplate && (
        <div data-testid="emoji-picker" className="rounded-lg border border-border bg-popover p-4 shadow-lg">
          {EMOJI_CATEGORIES.map((cat) => (
            <div key={cat.label} className="mb-4 last:mb-0">
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                {cat.label}
              </p>
              <div className="flex flex-wrap gap-1">
                {cat.emojis.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => {
                      insertAtCursor(emoji);
                      setShowEmojis(false);
                    }}
                    className="rounded-lg p-1.5 text-lg hover:bg-muted hover:scale-110 transition-all"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Placeholder chips summary — shown below textarea when placeholders are present */}
      {hasPlaceholdersInBody && !hasTemplate && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mr-1">Placeholders:</span>
          {placeholderNames.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 rounded-lg bg-primary/8 border border-primary/15 text-primary pl-2 pr-1 py-0.5 text-xs font-medium"
            >
              <Hash className="h-3 w-3" />
              {name}
              <button
                type="button"
                onClick={() => removePlaceholder(name)}
                className="ml-0.5 rounded-md hover:bg-primary/15 p-0.5 transition-colors"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Character counter & SMS segment info */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span className={cn(
            'flex items-center gap-1.5 tabular-nums',
            charWarning && 'text-warning font-semibold'
          )}>
            <Type className="h-3 w-3" />
            <span data-testid="char-count">{body.length} chars</span>
          </span>
          {body.length > 0 && smsSegments > 0 && (
            <span className="flex items-center gap-1.5 tabular-nums">
              <FileText className="h-3 w-3" />
              {smsSegments} segment{smsSegments !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {body.length > SMS_MULTIPART_LIMIT && (
          <span className="flex items-center gap-1 text-warning font-medium">
            <AlertTriangle className="h-3 w-3" />
            May be too long for some carriers
          </span>
        )}
      </div>
    </div>
  );
}

// --- Read-only template body with variable values shown inline ---
function TemplateReadOnly({
  body,
  templateVariables,
}: {
  body: string;
  templateVariables: Record<string, string>;
}) {
  // Parse body into text parts and variable parts
  const parts: ({ type: 'text'; value: string } | { type: 'var'; name: string })[] = [];
  const varPattern = /\{\{([\w-]+)\}\}/g;
  let match: RegExpExecArray | null;
  let lastIndex = 0;

  varPattern.lastIndex = 0;
  while ((match = varPattern.exec(body)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: body.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'var', name: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) {
    parts.push({ type: 'text', value: body.slice(lastIndex) });
  }

  return (
    <div className="rounded-lg border border-input bg-muted/15 px-4 py-4 text-sm leading-relaxed min-h-[120px]">
      {parts.map((part, i) => {
        if (part.type === 'text') {
          return <span key={i} className="whitespace-pre-wrap">{part.value}</span>;
        }

        const filled = templateVariables[part.name]?.trim();

        if (filled) {
          return (
            <span
              key={i}
              className="inline-block rounded-lg bg-primary/10 text-primary px-2 py-0.5 text-sm font-medium mx-0.5 ring-1 ring-primary/15"
            >
              {templateVariables[part.name]}
            </span>
          );
        }

        return (
          <span
            key={i}
            className="inline-block rounded-lg border border-dashed border-primary/40 bg-primary/5 text-primary/70 px-2 py-0.5 text-sm mx-0.5"
          >
            {`{{${part.name}}}`}
          </span>
        );
      })}
    </div>
  );
}
